import React, { Component, Fragment } from 'react'
import Modal from 'react-modal'
import Responsive from 'react-responsive'
import {
    CaptionText,
    H2,
    H4,
    SmallText,
    styleH2,
    Text
} from 'app/common/typography'
import styled from 'styled-components'
import ExecutionEnvironment from 'exenv'
import {
    breakpoints,
    ButtonSmall,
    media,
    ResponsiveContainer,
    style
} from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme/colors'
import { Restaurant, RestaurantCardMobile } from 'app/store-locator/restaurant'
import {
    CircleMap as CircleMapIcon,
    Filter as FilterIcon,
    List as ListIcon,
    Map as MapIcon,
    Spinner as SpinnerIcon
} from 'app/assets/icons'
import type { IntlShape } from 'react-intl'
import { FormattedMessage, injectIntl } from 'react-intl'
import type { Coordinates, SearchParams } from 'shared/store/store-locator'
import { useStaticStorelocator } from 'shared/store/store-locator'
import { Sticky, StickyContainer } from 'react-sticky'
import { Breadcrumb } from 'app/common/breadcrumb'
import type {
    FilterShortDTO,
    RestaurantMarkerDTO,
    RestaurantShortDTO
} from 'shared/types'
import classNames from 'classnames'
import { Layout } from 'app/common/layout'
import { Services } from './service'
import { ModalTitle } from 'app/common/modals'
import { DropdownButton } from 'app/common/dropdown'
import qs from 'qs'
import { ErrorManagement } from 'app/common/error-management'
import { getCurrentPosition } from 'shared/utils'
import {
    InputAddressAutocomplete,
    InputAddressAutocompleteDesktop
} from 'app/store-locator/input-address-autocomplete'
import { CURRENT_MAP_VIEW, MapWoosmap } from 'app-v2/modules/geolocation'

const { dropdownModalStyle } = style
const { grey, grey30, shadowBox, yellow, lightMayo } = oldColors

type Props = {
    restaurants: Array<RestaurantShortDTO>,
    markers: Array<RestaurantMarkerDTO>,
    filters: Array<FilterShortDTO>,
    loading: boolean,
    activeRestaurant: RestaurantShortDTO,
    mainNavigationOpen: boolean,
    originForDirections: ?Coordinates,
    hasSearchBeenMade: boolean,
    shouldDisplayLandingPage: boolean,
    resultsTitleKey: string,
    fetchPageData: () => Promise<any>,
    searchNearUserPosition: (p: SearchParams) => void,
    selectRestaurant: (r: RestaurantShortDTO) => void,
    setUserPosition: (position: Coordinates) => void,
    applyFilters: (Array<string>) => void,
    clearSearch: () => void,
    intl: IntlShape,
    location: any,
    error: any,
    networkError: any
}

type State = {
    showMap: boolean,
    showFilters: boolean,
    isRestaurantDetailsCollapsed: boolean,
    scrollPositionInListView: number,
    filters: Array<string>,
    place: ?string
}

class StoreLocator extends Component<Props, State> {
    constructor(props: Props) {
        super()
        const { location } = props
        const query = qs.parse(location.search, { ignoreQueryPrefix: true })
        this.state = {
            showMap: true,
            showFilters: false,
            isRestaurantDetailsCollapsed: true,
            scrollPositionInListView: 0,
            //$FlowFixMe
            filters: query.filters ? query.filters.split('|') : [],
            //$FlowFixMe
            place: query.place
        }
    }

    componentDidMount() {
        const {
            applyFilters,
            fetchPageData,
            hasSearchBeenMade,
            searchNearUserPosition,
            setUserPosition
        } = this.props
        const { filters } = this.state
        fetchPageData().then(
            () =>
                ExecutionEnvironment.canUseDOM &&
                getCurrentPosition({
                    applyFilters,
                    filters,
                    hasSearchBeenMade,
                    searchNearUserPosition,
                    setUserPosition,
                    geolocation: navigator.geolocation
                })
        )
    }

    componentWillUnmount() {
        this.props.clearSearch()
    }

    toggleMap = () => {
        if (!this.state.showMap) {
            this.setState({
                scrollPositionInListView: window.scrollY
            })
        }
        this.setState(prevState => ({
            showMap: !prevState.showMap
        }))
    }

    closeDropdown = () => {
        this.setState({ showFilters: false })
    }

    displayDropdown = () => {
        if (this.state.showFilters) {
            return
        }
        this.setState({ showFilters: true })
    }

    toggleRestaurantDetails = () => {
        this.setState(prevState => ({
            isRestaurantDetailsCollapsed: !prevState.isRestaurantDetailsCollapsed
        }))
    }

    selectRestaurant = (r: RestaurantShortDTO) => {
        this.toggleMap()
        this.props.selectRestaurant(r)
    }

    onSelectRestaurant = () =>
        this.setState({ isRestaurantDetailsCollapsed: true })

    render() {
        const {
            shouldDisplayLandingPage,
            restaurants,
            activeRestaurant,
            loading,
            mainNavigationOpen,
            originForDirections,
            resultsTitleKey,
            intl,
            error,
            networkError
        } = this.props

        const {
            showMap,
            showFilters,
            isRestaurantDetailsCollapsed,
            place
        } = this.state

        const nbRestaurants = restaurants ? restaurants.length : 0

        const breadcrumbTitle = intl.formatMessage({
            id: 'storelocator.title.breadcrumb.end'
        })

        const restaurantCardClasses = classNames({
            collapsed: isRestaurantDetailsCollapsed
        })

        const breakpoint = breakpoints.medium

        return (
            <ErrorManagement error={error} networkError={networkError}>
                <Layout
                    backgroundColor={lightMayo}
                    headerShouldStick={false}
                    withQuickSearch={false}
                    border>
                    <PreloadStoreLoc />
                    <Responsive maxWidth={breakpoint - 1}>
                        <StickyContainer>
                            <Sticky>
                                {({ style, isSticky }) => {
                                    const classes = classNames({
                                        stickyResults: isSticky && !showMap
                                    })
                                    return (
                                        <StickedContent
                                            style={style}
                                            className={classes}>
                                            <SearchContainer>
                                                <AddressSearchContainer>
                                                    <InputAddressAutocomplete
                                                        shadow
                                                        placeholder={
                                                            'storelocator.search.placeholder.mobile'
                                                        }
                                                        defaultSearch={place}
                                                        onSearch={address =>
                                                            this.setState({
                                                                place: address
                                                            })
                                                        }
                                                    />
                                                </AddressSearchContainer>
                                                <Toggle>
                                                    {showMap ? (
                                                        <ToggleButton
                                                            border
                                                            onClick={
                                                                this.toggleMap
                                                            }>
                                                            <ListIcon />
                                                            <FormattedMessage id="storelocator.toggle.list">
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <Text color="white">
                                                                        {text}
                                                                    </Text>
                                                                )}
                                                            </FormattedMessage>
                                                        </ToggleButton>
                                                    ) : (
                                                        <ToggleButton
                                                            border
                                                            onClick={
                                                                this.toggleMap
                                                            }>
                                                            <MapIcon />
                                                            <FormattedMessage id="storelocator.toggle.map">
                                                                {(
                                                                    text: string
                                                                ) => (
                                                                    <Text color="white">
                                                                        {text}
                                                                    </Text>
                                                                )}
                                                            </FormattedMessage>
                                                        </ToggleButton>
                                                    )}
                                                    <ToggleButton
                                                        onClick={
                                                            this.displayDropdown
                                                        }>
                                                        <FilterIcon />
                                                        <FormattedMessage id="storelocator.toggle.filters">
                                                            {(text: string) => (
                                                                <Text color="white">
                                                                    {text}
                                                                </Text>
                                                            )}
                                                        </FormattedMessage>
                                                    </ToggleButton>
                                                </Toggle>
                                            </SearchContainer>
                                        </StickedContent>
                                    )
                                }}
                            </Sticky>
                            {showMap ? (
                                <MapWoosmap
                                    onSelectRestaurant={this.onSelectRestaurant}
                                    ContainerMap={MapContainer}
                                    currentView={CURRENT_MAP_VIEW.STORE_LOCATOR}
                                />
                            ) : loading ? (
                                <DefaultContainer>
                                    <SpinnerIcon size={40} />
                                </DefaultContainer>
                            ) : nbRestaurants > 0 ? (
                                <RestaurantsScrollView
                                    restaurants={restaurants}
                                    activeRestaurant={activeRestaurant}
                                    selectRestaurant={this.selectRestaurant}
                                />
                            ) : (
                                <DefaultContainer>
                                    <FormattedMessage id="storelocator.search.results.none.title">
                                        {(text: string) => <H4>{text}</H4>}
                                    </FormattedMessage>
                                    <FormattedMessage id="storelocator.search.results.none.baseline">
                                        {(text: string) => (
                                            <CaptionText>{text}</CaptionText>
                                        )}
                                    </FormattedMessage>
                                    <CTA>
                                        <FormattedMessage id="storelocator.search.results.none.cta">
                                            {(text: string) => (
                                                <ButtonSmall
                                                    onClick={this.toggleMap}>
                                                    {text}
                                                </ButtonSmall>
                                            )}
                                        </FormattedMessage>
                                    </CTA>
                                </DefaultContainer>
                            )}
                        </StickyContainer>
                        {!mainNavigationOpen && showMap && activeRestaurant && (
                            <RestaurantCardContainer
                                className={restaurantCardClasses}>
                                <RestaurantCardMobile
                                    restaurant={activeRestaurant}
                                    collapsed={isRestaurantDetailsCollapsed}
                                    handleToggle={this.toggleRestaurantDetails}
                                    origin={originForDirections}
                                />
                            </RestaurantCardContainer>
                        )}
                        <Modal
                            isOpen={showFilters}
                            ariaHideApp={false}
                            style={dropdownModalStyle}>
                            <ResponsiveContainer>
                                <ModalTitle
                                    titleNode={
                                        <StyledH2>
                                            {intl.formatMessage({
                                                id: 'modal.filters.title'
                                            })}
                                        </StyledH2>
                                    }
                                    handleClose={this.closeDropdown}
                                />
                                <Services close={this.closeDropdown} />
                            </ResponsiveContainer>
                        </Modal>
                    </Responsive>
                    <Responsive minWidth={breakpoint}>
                        <Row>
                            <Column left landing={shouldDisplayLandingPage}>
                                <Content centered={shouldDisplayLandingPage}>
                                    {shouldDisplayLandingPage ? (
                                        <Fragment>
                                            <LandingPageBreadcrumb
                                                color="white"
                                                breadcrumbArray={[
                                                    {
                                                        title: breadcrumbTitle
                                                    }
                                                ]}
                                            />
                                            <LandingPageContent>
                                                <CircleMapIcon size={80} />
                                                <Title centered>
                                                    <FormattedMessage id="storelocator.title.landing" />
                                                </Title>
                                                <AddressSearchContainer desktop>
                                                    <InputAddressAutocompleteDesktop
                                                        defaultSearch={place}
                                                        iconColor={grey}
                                                        onSearch={address =>
                                                            this.setState({
                                                                place: address
                                                            })
                                                        }
                                                    />
                                                </AddressSearchContainer>
                                            </LandingPageContent>
                                        </Fragment>
                                    ) : (
                                        <Fragment>
                                            <Breadcrumb
                                                breadcrumbArray={[
                                                    {
                                                        title: breadcrumbTitle
                                                    }
                                                ]}
                                            />
                                            <Title>
                                                <FormattedMessage id="storelocator.title" />
                                            </Title>
                                            <AddressSearchContainer desktop>
                                                <InputAddressAutocompleteDesktop
                                                    defaultSearch={place}
                                                    iconColor={grey}
                                                    onSearch={address =>
                                                        this.setState({
                                                            place: address
                                                        })
                                                    }
                                                />
                                            </AddressSearchContainer>
                                            <ResultsTitleContainer>
                                                {loading ? (
                                                    <SpinnerIcon size={24} />
                                                ) : (
                                                    <ResultsTitle color={grey}>
                                                        <FormattedMessage
                                                            values={{
                                                                number: activeRestaurant
                                                                    ? 1
                                                                    : nbRestaurants
                                                            }}
                                                            id={`storelocator.results.title.${resultsTitleKey}`}
                                                        />
                                                    </ResultsTitle>
                                                )}
                                                <StyledDropdownButtonContainer
                                                    onClick={
                                                        this.displayDropdown
                                                    }>
                                                    <DropdownButton
                                                        label={intl.formatMessage(
                                                            {
                                                                id:
                                                                    'storelocator.filters.by.label'
                                                            }
                                                        )}
                                                        collapsed={showFilters}>
                                                        <Fragment>
                                                            {showFilters && (
                                                                <Services
                                                                    clickOutside={
                                                                        true
                                                                    }
                                                                    close={
                                                                        this
                                                                            .closeDropdown
                                                                    }
                                                                />
                                                            )}
                                                        </Fragment>
                                                    </DropdownButton>
                                                </StyledDropdownButtonContainer>
                                            </ResultsTitleContainer>
                                            {activeRestaurant ? (
                                                <Restaurant
                                                    id={`restaurant-${activeRestaurant.id}`}
                                                    restaurant={
                                                        activeRestaurant
                                                    }
                                                    active={true}
                                                    collapsible={false}
                                                    onSelectRestaurant={
                                                        this.onSelectRestaurant
                                                    }
                                                />
                                            ) : (
                                                // Done an address research
                                                // -> Display results list
                                                <RestaurantsScrollView
                                                    restaurants={restaurants}
                                                    activeRestaurant={
                                                        activeRestaurant
                                                    }
                                                    selectRestaurant={
                                                        this.selectRestaurant
                                                    }
                                                />
                                            )}
                                        </Fragment>
                                    )}
                                </Content>
                            </Column>
                            <Column>
                                {/* <Map
                                    containerElement={<MapContainerDesktop />}
                                    mapElement={<div className="full-height" />}
                                    onSelectRestaurant={this.onSelectRestaurant}
                                /> */}
                                <MapWoosmap
                                    onSelectRestaurant={this.onSelectRestaurant}
                                    ContainerMap={MapContainerDesktop}
                                    currentView={CURRENT_MAP_VIEW.STORE_LOCATOR}
                                />
                            </Column>
                        </Row>
                    </Responsive>
                </Layout>
            </ErrorManagement>
        )
    }
}

const PreloadStoreLoc = () => {
    //TODO: we should convert the parent component to a functional component and inject the services from there
    // instead of just warming the cache here
    useStaticStorelocator()
    return null
}

type RSVProps = {
    restaurants: Array<RestaurantShortDTO>,
    activeRestaurant: RestaurantShortDTO,
    selectRestaurant: (r: RestaurantShortDTO) => void
}
const RestaurantsScrollView = (props: RSVProps) => {
    const { restaurants, activeRestaurant, selectRestaurant } = props

    return (
        <ScrollView className="scroll-view">
            {restaurants &&
                restaurants.map((restaurant: RestaurantShortDTO, index) => (
                    <Restaurant
                        id={`restaurant-${restaurant.id}`}
                        key={index}
                        collapsible={true}
                        restaurant={restaurant}
                        active={
                            activeRestaurant &&
                            activeRestaurant.id === restaurant.id
                        }
                        onSelectRestaurant={() => selectRestaurant(restaurant)}
                    />
                ))}
        </ScrollView>
    )
}

const LandingPageBreadcrumb = styled(Breadcrumb)`
    color: white;
`

const LandingPageContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex-grow: 1;
`

const ScrollView = styled(ResponsiveContainer)`
    ${media.small`
        min-height: 440px;
        height: calc(100vh - 390px);
        overflow: hidden;
        overflow-y: scroll;
        width: 100%;
        margin: 0;
    `};
`

const Toggle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 20px;
    background-color: ${yellow};
    max-width: 190px;
    margin: auto;
    height: 30px;
`

const ToggleButton = styled.div`
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: transparent;
    box-sizing: border-box;
    width: 50%;
    ${style.button};
    ${props => (props.border ? 'border-right: 1px solid white;' : '')};
`

const ResultsTitle = styled(SmallText)`
    display: block;
    color: ${grey};
`

const Title = styled.h1`
    ${styleH2} margin-top: 10px;
    white-space: pre;
    text-align: ${props => (props.centered ? 'center' : 'inherit')};
`

const MapContainer = styled.div`
    height: 80vh;
    margin-top: -130px;
`

const MapContainerDesktop = styled.div`
    width: 100%;
    height: 100%;
`

const StickedContent = styled.div`
    position: relative;
    z-index: 1;

    &.stickyResults {
        background-color: rgba(255, 255, 255, 0.6);
        backdrop-filter: blur(20px);
    }
`
const SearchContainer = styled(ResponsiveContainer)`
    padding: 24px 0 20px;
`

const AddressSearchContainer = styled.div`
    width: 100%;
    padding: ${props => (props.desktop ? '30px 0' : '0 0 15px 0')};
`

const DefaultContainer = styled(ResponsiveContainer)`
    display: flex;
    flex-direction: column;
    min-height: 70vh;
    align-items: center;
    justify-content: center;
`

const RestaurantCardContainer = styled.div`
    position: fixed;
    bottom: 0;
    background: white;
    border-radius: 10px 10px 0 0;
    box-shadow: 0 8px 16px 4px ${shadowBox};
    z-index: 100;
    width: 100%;
`

const CTA = styled.div`
    padding: 30px;
`

const Row = styled.section`
    width: 100%;
    min-height: 85vh;
    display: flex;
`

const Column = styled.div`
    flex-basis: 50%;
    display: flex;
    justify-content: ${props => (props.left ? 'flex-end' : 'flex-start')};
    align-items: stretch;
    background-color: ${props =>
        props.left && props.landing ? colors.fieryRed : 'none'};
    min-height: 85vh;
`

const Content = styled.div`
    box-sizing: border-box;
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 32px;
    width: 100%;
    ${media.medium`
        padding-top: 64px;
    `};
    ${media.large`
        width: 640px;
        padding-left: 0;
        padding-right: 116px;
    `};

    ${props => {
        if (props.centered)
            return `
            color: white;
            display: flex;
            flex-direction: column;
        `
    }};
`

const ResultsTitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    ${media.small`
        padding-bottom: 20px;
        border-bottom: solid 1px ${grey30};
    `};
`

const StyledDropdownButtonContainer = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;
    position: relative;
    margin-left: auto;
`

const StyledH2 = styled(H2)`
    margin: 0;
`

export const StoreLocatorComponent = injectIntl(StoreLocator)
